import { useState } from "react";
import { Icone } from "@/components/Icone";
import { Avatar } from "./Avatar";
import { Etiquetas } from "./Etiquetas";
import { FormNota } from "./FormNota";
import { comoChamar, formatarTelefone } from "@/lib/painel/telefone";
import type { DetalheConversa } from "@/lib/telas/conversas";

/**
 * A coluna da direita: quem é essa pessoa.
 *
 * Etapa do funil, ficha, etiquetas, agendamentos e as notas da equipe — tudo o
 * que a recepção precisa saber antes de responder, sem sair da conversa.
 * Numa tela larga ela fica ao lado; numa estreita abre por cima, e `onFechar`
 * é o X do canto.
 */
export function FichaLateral({
  det,
  sugestoes,
  nomes,
  userIdAtual,
  onFechar,
  className,
}: {
  det: DetalheConversa;
  sugestoes: string[];
  nomes: Record<string, string>;
  userIdAtual: string;
  onFechar: () => void;
  className?: string;
}) {
  const [copiado, setCopiado] = useState(false);
  const limpo = det.telefone.replace(/\D/g, "");

  return (
    <aside
      aria-label="Ficha do paciente"
      className={"min-h-0 overflow-y-auto border-[var(--color-borda)] bg-[var(--color-cartao)] text-[var(--color-tinta)] " + (className ?? "")}
    >
      <div className="relative flex flex-col items-center gap-1 border-b border-[var(--color-borda)] px-5 pt-6 pb-5 text-center">
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar a ficha"
          className="absolute top-2.5 right-2.5 flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-suave)] hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]"
        >
          <Icone nome="fechar" className="h-[18px] w-[18px]" />
        </button>
        <Avatar iniciais={det.iniciais} telefone={det.telefone} tamanho="lg" />
        <p className="mt-2 text-lg font-bold">{comoChamar(det.nome, det.telefone)}</p>
        <p className="font-mono text-[12.5px] text-[var(--color-suave)]">{formatarTelefone(det.telefone)}</p>
        <div className="mt-2.5 flex gap-2">
          <a
            href={`https://wa.me/${limpo}`}
            target="_blank"
            rel="noreferrer"
            className="flex h-8 items-center gap-1.5 rounded-full bg-[var(--color-cartao)] px-3 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao hover:bg-[var(--color-painel)]"
          >
            <Icone nome="whatsapp" className="h-4 w-4" />
            Abrir no WhatsApp
          </a>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(det.telefone);
              setCopiado(true);
              setTimeout(() => setCopiado(false), 2000);
            }}
            className="flex h-8 items-center gap-1.5 rounded-full bg-[var(--color-cartao)] px-3 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao hover:bg-[var(--color-painel)]"
          >
            <Icone nome={copiado ? "check" : "copiar"} className="h-4 w-4" />
            {copiado ? "Copiado" : "Copiar"}
          </button>
        </div>
      </div>

      {det.ficha && (
        <Secao>
          <div className="rounded-xl bg-[var(--color-painel)] p-3.5">
            <div className="flex justify-between text-xs font-semibold text-[var(--color-fraco)]">
              <span>Etapa do funil</span>
              <span className="num">
                {det.ficha.etapaIndice} de {det.ficha.etapaTotal}
              </span>
            </div>
            <p className="mt-0.5 mb-2.5 text-base font-bold">{det.ficha.etapa}</p>
            <div className="flex gap-1">
              {Array.from({ length: det.ficha.etapaTotal }).map((_, i) => (
                <span
                  key={i}
                  className={"h-1.5 flex-1 rounded-full " + (i < det.ficha!.etapaIndice ? "bg-[var(--color-marca)]" : "bg-[var(--color-borda-forte)]")}
                />
              ))}
            </div>
          </div>
        </Secao>
      )}

      <Secao titulo="Ficha">
        {det.ficha ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-2 text-[13.5px]">
            <Linha k="Interesse" v={det.ficha.interesse ?? "—"} />
            <Linha k="Agendou?" v={det.ficha.etapaIndice >= 4 ? "sim" : "ainda não"} />
            <Linha k="Primeiro contato" v={det.ficha.primeiroContato} />
            <Linha k="Observações" v={det.ficha.observacoes ?? "nenhuma"} />
          </dl>
        ) : (
          <p className="text-[13.5px] text-[var(--color-suave)]">
            Sem ficha ainda. O assistente cria assim que a pessoa disser o nome ou o que procura.
          </p>
        )}
      </Secao>

      <Secao titulo="Etiquetas">
        <Etiquetas telefone={det.telefone} etiquetas={det.etiquetas} sugestoes={sugestoes} />
      </Secao>

      <Secao titulo="Agendamentos">
        {det.agendamentos.length === 0 ? (
          <p className="text-[13.5px] text-[var(--color-suave)]">Nenhum horário marcado para esta pessoa.</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {det.agendamentos.map((a) => {
              const [diaNum, mesNum] = a.dia.split("/");
              return (
                <li key={a.id} className={"flex items-center gap-3 " + (a.jaPassou ? "opacity-70" : "")}>
                  <span className="flex h-12 w-11 shrink-0 flex-col items-center justify-center rounded-[10px] bg-[var(--color-realce)] leading-tight text-[var(--color-marca-forte)]">
                    <b className="text-[17px]">{diaNum ?? a.dia}</b>
                    <small className="text-[10.5px] font-bold">{MESES[Number(mesNum) - 1] ?? mesNum}</small>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                      {a.hora} · {a.titulo}
                    </p>
                    <p className="truncate text-[13px] text-[var(--color-suave)]">
                      {a.profissional}
                      {a.jaPassou ? " · já passou" : ""}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Secao>

      <Secao titulo="Notas da equipe" ultima>
        <FormNota telefone={det.telefone} />
        {det.notas.length > 0 && (
          <ul className="mt-1 flex flex-col gap-2">
            {det.notas.map((n) => (
              <li key={n.id} className="rounded-[10px] bg-[var(--color-nota)] px-3 py-2.5 text-[13px] text-[var(--color-nota-tinta)]">
                <p className="whitespace-pre-wrap">{n.texto}</p>
                <p className="mt-0.5 text-[11.5px] opacity-70">
                  {n.autorUserId === userIdAtual ? "você" : (nomes[n.autorUserId] ?? "equipe")} · {n.quando}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </aside>
  );
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function Secao({ titulo, children, ultima }: { titulo?: string; children: React.ReactNode; ultima?: boolean }) {
  return (
    <section className={"flex flex-col gap-2.5 px-5 py-4 " + (ultima ? "" : "border-b border-[var(--color-borda)]")}>
      {titulo && <h3 className="rotulo">{titulo}</h3>}
      {children}
    </section>
  );
}

function Linha({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-[var(--color-fraco)]">{k}</dt>
      <dd className="text-right font-medium">{v}</dd>
    </>
  );
}
